/**
 * Shared hooks for the two bespoke restaurant tables (`ChainsTableComponent`
 * and `DoorsTableComponentV2`) — the elastic-search sort choreography, the
 * tag/distributor/GPO bulk-action callbacks, and the unified-summary refetch
 * were byte-for-byte duplicated between them (FIR-6997 consolidation).
 *
 * Both tables are slated to fold into the config-driven `<TargetTable>`
 * (FIR-7728); until then this keeps the pair in lockstep.
 */

import { QueryClient } from '@tanstack/react-query'
import { SortOption } from 'components/Buttons/TableButtons/TableButtonSort'
import {
  DataTableColumnSort,
  dataTableSortingStoreRepo,
} from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import { useCallback, useEffect, useMemo } from 'react'
import useApiService from 'services/api'
import { ManageTagsDistributorsParams } from 'services/common_table_actions'
import { UnifiedTargetType } from 'services/opportunities/targetTypeMap'
import { TableTypeSlug } from 'models/companies'
import { paramsContainAccountFilters } from 'utils/filterUtils'

type ApiService = ReturnType<typeof useApiService>

/**
 * Elastic-search sort choreography shared by the restaurant tables:
 *
 *  - seeds the default sort (`fallbackSortId` desc) once on mount,
 *  - resets a lingering `relevancy` sort back to the fallback when the search
 *    is cleared (relevancy is meaningless without a query), and
 *  - hides the `relevancy` option from the sort dropdown while no search is
 *    active.
 *
 * @param tableKey sorting-store key for this table instance
 * @param search the filter store's search state (keyed search boxes)
 * @param fallbackSortId column id sorted desc by default / after search clears
 * @param sortableFieldOptions full dropdown option list (incl. relevancy)
 */
export function useElasticRelevancySort(
  tableKey: string,
  search: Record<string, { value?: string } | undefined>,
  fallbackSortId: string,
  sortableFieldOptions: readonly SortOption<string>[]
): {
  sorting: DataTableColumnSort[]
  setSorting: (sorting: DataTableColumnSort[]) => void
  filteredSortOptions: SortOption<string>[]
} {
  const { sorting, setSorting } = dataTableSortingStoreRepo.getStore(tableKey)()

  // Seed the default sort once on mount (external sorting-store sync).
  useEffect(() => {
    if (!sorting.length) {
      setSorting([{ id: fallbackSortId, desc: true }])
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const hasActiveSearch = useMemo(() => {
    return Object.keys(search).some((key) => search[key]?.value)
  }, [search])

  // Reset relevancy sort to the fallback when search is cleared (sync with the
  // external filter/sorting stores).
  useEffect(() => {
    if (!hasActiveSearch && sorting.length && sorting[0].id === 'relevancy') {
      setSorting([{ id: fallbackSortId, desc: true }])
    }
  }, [hasActiveSearch, sorting, setSorting, fallbackSortId])

  const filteredSortOptions = useMemo(() => {
    if (hasActiveSearch) {
      return [...sortableFieldOptions]
    }
    return sortableFieldOptions.filter((opt) => opt.value !== 'relevancy')
  }, [hasActiveSearch, sortableFieldOptions])

  return { sorting, setSorting, filteredSortOptions }
}

/** `{ sort: '-col' }` param from the table's first sort entry. */
export function useSortParams(
  sorting: DataTableColumnSort[] | undefined
): Record<string, unknown> {
  return useMemo(() => {
    const params: Record<string, unknown> = {}
    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }
    return params
  }, [sorting])
}

/**
 * Refetch the unified summary key that backs this table's stat cards, but only
 * when the active filters contain account-level filters (the only case where a
 * mutation can change the summary through the current filter set).
 */
export function useUnifiedSummaryRefetch(
  queryClient: QueryClient,
  summaryKey: string,
  filterAndSortParams: Record<string, unknown>
): () => void {
  return useCallback(() => {
    if (!paramsContainAccountFilters(filterAndSortParams)) return
    queryClient.refetchQueries({
      queryKey: [summaryKey],
    })
  }, [filterAndSortParams, queryClient, summaryKey])
}

/**
 * The tag / distributor / GPO bulk-action callbacks the assignment modals
 * take, routed through the unified engine for this target type and followed by
 * a summary refetch (membership/LTV may have changed).
 */
export function useManageAssignmentActions(options: {
  api: ApiService
  tableType: TableTypeSlug
  // Optional: when omitted the actions route to the `tableType` slug endpoint
  // (e.g. `reports/chains/*`); when set they route through the unified engine
  // for that target type.
  unifiedTargetType?: UnifiedTargetType
  unifiedDoors?: boolean
  refetchSummary: () => void
}): {
  apiTagsAction: (values: ManageTagsDistributorsParams) => Promise<unknown>
  apiDistributorsAction: (
    values: ManageTagsDistributorsParams
  ) => Promise<unknown>
  apiGPOsAction: (values: ManageTagsDistributorsParams) => Promise<unknown>
} {
  const { api, tableType, unifiedTargetType, unifiedDoors, refetchSummary } =
    options

  const apiTagsAction = useCallback(
    async (values: ManageTagsDistributorsParams) => {
      const response = await api.commonManageTags({
        tableType,
        values,
        unifiedTargetType,
        ...(unifiedDoors ? { unifiedDoors } : {}),
      })
      refetchSummary()
      return response
    },
    [api, refetchSummary, tableType, unifiedTargetType, unifiedDoors]
  )

  const apiDistributorsAction = useCallback(
    async (values: ManageTagsDistributorsParams) => {
      const response = await api.commonManageDistributors({
        tableType,
        values,
        unifiedTargetType,
        ...(unifiedDoors ? { unifiedDoors } : {}),
      })
      refetchSummary()
      return response
    },
    [api, refetchSummary, tableType, unifiedTargetType, unifiedDoors]
  )

  const apiGPOsAction = useCallback(
    async (values: ManageTagsDistributorsParams) => {
      const response = await api.commonManageGPOs({
        tableType,
        values,
        unifiedTargetType,
        ...(unifiedDoors ? { unifiedDoors } : {}),
      })
      refetchSummary()
      return response
    },
    [api, refetchSummary, tableType, unifiedTargetType, unifiedDoors]
  )

  return { apiTagsAction, apiDistributorsAction, apiGPOsAction }
}
